import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useCustomer } from '../contexts/customer-context';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Fade,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { createAccount } from '../utils/api';

// Styled components (same as in CustomerLoginPage)
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
  borderRadius: '16px',
  background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
  transition: 'all 0.3s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const Logo = styled('img')(({ theme }) => ({
  width: '150px',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '120px',
    marginBottom: '15px',
  },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const ConsumerSignupPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { updateCustomerId, updateCustomerEmail } = useCustomer();

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setError('');
  };

  const validatePassword = (password) => {
    const minLength = 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validatePassword(password)) {
      setError('Password must be at least 6 characters long and include uppercase, lowercase, and numbers.');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    try {
      const response = await createAccount(email, password);
      console.log('Account created:', response);
      if (response.userId) {
        updateCustomerId(response.userId);
        updateCustomerEmail(email);
        navigate('/welcome');
      } else {
        setError(response.message || 'Failed to create account. Please try again.');
      }
    } catch (error) {   
      
      if (error.response && error.response.status === 400) {
        setError(error.response.data.message);
      } else {
        console.error('Account creation error:', error);
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <Fade in={true} timeout={1000}>
        <StyledPaper elevation={3}>
          <Logo src="./Aviora-logo.png" alt="Company Logo" />
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Create Account
          </Typography>
          
          <Box sx={{ mt: 2, mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <PersonAddIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'secondary.main', mb: 2 }} />
            <Typography variant="body1" align="center" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Join us today and start enjoying our services!
            </Typography>
          </Box>

          <Box sx={{ mt: 1, width: '100%' }}>
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                required
                margin="normal"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
                margin="normal"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                margin="normal"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              {error && <ErrorText>{error}</ErrorText>}
              <Button 
                type="submit" 
                variant="contained" 
                color="secondary" 
                fullWidth 
                disabled={loading}
                sx={{ 
                  mt: 3, 
                  py: { xs: 1, sm: 1.5 }, 
                  fontWeight: 600, 
                  fontSize: { xs: '0.9rem', sm: '1rem' }, 
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
                  '&:hover': { 
                    transform: 'translateY(-2px)', 
                    boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)' 
                  } 
                }}
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </Button>
            </form>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link component={RouterLink} to="/login" variant="body2" sx={{ color: 'secondary.main' }}>
                  Log in
                </Link>
              </Typography>
            </Box>
          </Box>
        </StyledPaper>
      </Fade>
    </Container>
  );
};

export default ConsumerSignupPage;